<script setup>
import { onMounted, ref } from 'vue'
import { downloadReportPdf, getAdminReportResults, getReportTemplates } from '../../api/reportApi.js'
import AppAlert from '../../shared/components/AppAlert.vue'
import ResultHistoryTable from '../history/components/ResultHistoryTable.vue'
import AuditEventHistory from './AuditEventHistory.vue'
import HealthCoverageLinkSettings from './HealthCoverageLinkSettings.vue'
import { canDownloadPdf } from '../history/historyUtils.js'
import { createTemplateSelectionMap, findDefaultTemplate, includeTemplateInFilename } from '../../report/reportTemplateUtils.js'

const PAGE_SIZE = 20
const results = ref([])
const page = ref(0)
const totalElements = ref(0)
const totalPages = ref(0)
const isLoading = ref(false)
const error = ref('')
const downloadingReportIds = ref(new Set())
const templates = ref([])
const selectedTemplateIds = ref({})
const templateErrors = ref({})
const isTemplateLoading = ref(true)
const activeTab = ref('history')

function syncTemplateSelections() {
  const defaultTemplateId = findDefaultTemplate(templates.value)?.templateId || ''
  selectedTemplateIds.value = createTemplateSelectionMap(results.value, selectedTemplateIds.value, defaultTemplateId)
}

async function loadTemplates() {
  isTemplateLoading.value = true
  try {
    const response = await getReportTemplates()
    templates.value = response.templates || []
    syncTemplateSelections()
  } catch (cause) {
    error.value = cause.message || '리포트 종류를 불러오지 못했습니다.'
  } finally {
    isTemplateLoading.value = false
  }
}

async function loadHistory(targetPage = 0) {
  if (isLoading.value) return
  isLoading.value = true
  error.value = ''
  try {
    const response = await getAdminReportResults(targetPage, PAGE_SIZE)
    results.value = response.content || []
    syncTemplateSelections()
    page.value = response.page
    totalElements.value = response.totalElements
    totalPages.value = response.totalPages
  } catch (cause) {
    error.value = cause.message || '전체 결과이력을 불러오지 못했습니다.'
  } finally {
    isLoading.value = false
  }
}

async function downloadPdf(result) {
  if (!canDownloadPdf(result) || downloadingReportIds.value.has(result.reportId)) return
  const templateId = selectedTemplateIds.value[result.reportId]
  if (!templateId) return
  downloadingReportIds.value = new Set(downloadingReportIds.value).add(result.reportId)
  templateErrors.value = { ...templateErrors.value, [result.reportId]: '' }
  try {
    const download = await downloadReportPdf(result.reportId, templateId)
    const url = URL.createObjectURL(download.blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = includeTemplateInFilename(
      download.filename || `건강자산_보장분석_${result.uniqueNumber}.pdf`,
      templateId
    )
    anchor.click()
    window.setTimeout(() => URL.revokeObjectURL(url), 1000)
  } catch (cause) {
    templateErrors.value = {
      ...templateErrors.value,
      [result.reportId]: cause.message || 'PDF를 생성하지 못했습니다.'
    }
  } finally {
    const remainingIds = new Set(downloadingReportIds.value)
    remainingIds.delete(result.reportId)
    downloadingReportIds.value = remainingIds
  }
}

function changeTemplate(reportId, templateId) {
  selectedTemplateIds.value = { ...selectedTemplateIds.value, [reportId]: templateId }
  templateErrors.value = { ...templateErrors.value, [reportId]: '' }
}

onMounted(async () => {
  await Promise.all([loadTemplates(), loadHistory()])
  syncTemplateSelections()
})
</script>

<template>
  <section class="history-page">
    <div class="admin-tabs" role="tablist" aria-label="관리자 설정">
      <button type="button" role="tab" :aria-selected="activeTab === 'history'" @click="activeTab = 'history'">전체 결과이력</button>
      <button type="button" role="tab" :aria-selected="activeTab === 'links'" @click="activeTab = 'links'">건강 영역 · 보장 연결</button>
      <button type="button" role="tab" :aria-selected="activeTab === 'audit'" @click="activeTab = 'audit'">감사 이력</button>
    </div>

    <HealthCoverageLinkSettings v-if="activeTab === 'links'" />

    <AuditEventHistory v-else-if="activeTab === 'audit'" />

    <template v-else>
      <div class="history-heading">
        <div>
          <p class="home-kicker">관리자 설정</p>
          <h1>전체 결과이력</h1>
          <p class="page-lead">모든 사용자의 산출 결과를 확인하고 PDF를 받을 수 있습니다.</p>
        </div>
        <div class="history-summary"><div><span>전체 결과</span><b>{{ totalElements }}건</b></div></div>
      </div>

      <AppAlert v-if="error">{{ error }}</AppAlert>

      <ResultHistoryTable
        :results="results"
        :page="page"
        :page-size="PAGE_SIZE"
        :total-elements="totalElements"
        :total-pages="totalPages"
        :is-loading="isLoading"
        :downloading-report-ids="downloadingReportIds"
        :templates="templates"
        :selected-template-ids="selectedTemplateIds"
        :template-errors="templateErrors"
        :is-template-loading="isTemplateLoading"
        show-admin-details
        @download="downloadPdf"
        @template-change="changeTemplate"
        @page-change="loadHistory"
      />
    </template>
  </section>
</template>
